/**
 * Форматирование чисел и дат для графика динамики фонда.
 *
 * Порт хелперов легаси `components/aton/chart/templates/.default/settings.js`
 * (`number_format`, `getDate`). Формулы и крайние случаи воспроизведены один
 * в один: цифры в плашке доходности и в тултипе должны совпадать со старой
 * страницей до последнего знака.
 */

/** Порог, с которого легаси сокращает число до миллионов. */
const MILLION = 1_000_000;

/** Подпись валюты в тултипе и на оси — ключи `A_CHART_T_CURRENCY_*` легаси. */
const CURRENCY_LABELS: Record<string, string> = {
  RUR: 'руб.',
  RUB: 'руб.',
  USD: 'дол.',
  EUR: 'евро',
  CNY: '¥',
};

/** Легаси нормализует `RUB` к `RUR` — под этим кодом лежат подписи и периоды. */
export const normalizeCurrency = (code: string): string => (code === 'RUB' ? 'RUR' : code);

export const currencyLabel = (code: string): string =>
  CURRENCY_LABELS[normalizeCurrency(code)] ?? code;

/**
 * `number_format` из легаси.
 *
 * @param toMillionRound числа от миллиона делятся на миллион и получают « млн»
 * @param addPrefix     положительные получают `+ `, отрицательные — `— `
 */
export const numberFormat = (
  value: number,
  round: number,
  decPoint = '.',
  thousandsSep = ' ',
  toMillionRound = false,
  addPrefix = false,
): string => {
  const source = Number.isFinite(value) ? value : 0;
  const isLower = Math.abs(source) < MILLION;
  const number = toMillionRound && !isLower ? source / MILLION : source;
  const precision = Math.abs(round);
  const million = toMillionRound && !isLower ? ' млн' : '';

  const factor = 10 ** precision;
  const fixed = precision
    ? (Math.round(number * factor) / factor).toFixed(precision)
    : String(Math.round(number));

  const parts = fixed.split('.');
  const int = (parts[0] ?? '').replace(/\B(?=(?:\d{3})+(?!\d))/g, thousandsSep);
  const fraction = (parts[1] ?? '').padEnd(precision, '0');

  let result = precision ? `${int}${decPoint}${fraction}` : int;

  if (addPrefix) {
    // Легаси именно так: минус превращается в длинное тире с пробелом,
    // ноль остаётся без знака.
    result = result.includes('-')
      ? result.replace('-', '— ')
      : result !== '0'
        ? `+ ${result}`
        : result;
  }

  return result + million;
};

/**
 * Число знаков после запятой для процента в тултипе.
 *
 * Легаси сначала форматирует процент с тремя знаками и пробелом-разделителем
 * тысяч, а потом прогоняет строку через `Number()`. Для значений от тысячи
 * получается `NaN`, все сравнения ложны — и остаётся три знака. Это заметно
 * на графике «С основания» (`+ 3 047,260 %`), поэтому квирк сохранён.
 */
export const tooltipPercentRound = (percent: number): number => {
  const probe = Math.abs(Number(numberFormat(percent, 3, '.', ' ', true)));

  if (probe > 0.01) return 2;

  return probe === 0 ? 0 : 3;
};

/**
 * `dd.mm.yyyy` — как `aChart.getDate()`. Точки ряда лежат на UTC-полуночи.
 *
 * Числовой формат здесь оставлен намеренно: это ось графика, тултип и поле
 * календаря, куда длинные названия месяцев не влезают. Текстовые даты фондов
 * («Цена на …», «Дата запуска фонда») идут через общий `useDateFormat`.
 */
export const formatDate = (ms: number): string => {
  const date = new Date(ms);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');

  return `${day}.${month}.${date.getUTCFullYear()}`;
};

/**
 * Дата из ответа API в таймстемп UTC-полуночи.
 *
 * Бэкенд отдаёт `1997-04-21T00:00:00+04:00` — со сдвигом того года. Легаси же
 * клало в график UTC-полночь той же календарной даты, поэтому берём только
 * дату и отбрасываем смещение: иначе точки «уезжают» на час и подписи осей
 * расходятся со старой страницей.
 */
export const toUtcMidnight = (iso: string | undefined): number | null => {
  if (!iso) return null;

  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);

  if (!year || !month || !day) return null;

  return Date.UTC(year, month - 1, day);
};

/**
 * Сокращение больших сумм: млрд / млн / без сокращения.
 * Легаси использует его для СЧА — в тултипе, на оси Y и в плашке.
 */
export const formatLargeAmount = (value: number): string => {
  const absolute = Math.abs(value);

  if (absolute >= 1_000_000_000) {
    return `${numberFormat(value / 1_000_000_000, 2, ',', ' ')} млрд`;
  }

  if (absolute >= MILLION) {
    return `${numberFormat(value / MILLION, 2, ',', ' ')} млн`;
  }

  return numberFormat(value, 2, ',', ' ');
};
